import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { RootState } from '../../store'
import { loginRequest, clearError } from '../../store/actions/authActions'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { authService } from '../../services/authService'

import { loginSuccess } from '../../store/actions/authActions'
import { Sparkles, Mail, Lock } from 'lucide-react'

interface LoginFormData {
  email: string
  password: string
}

export const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isLoading: authLoading, error, isAuthenticated, user } = useSelector((state: RootState) => state.auth)
  const [googleLoading, setGoogleLoading] = React.useState(false) // Add state for Google loading
  const [mfaRequired, setMfaRequired] = React.useState(false)
  const [mfaToken, setMfaToken] = React.useState('')
  const [mfaCode, setMfaCode] = React.useState('')
  const [isBackupMode, setIsBackupMode] = React.useState(false)
  const [backupCode, setBackupCode] = React.useState('')
  const [localLoading, setLocalLoading] = React.useState(false)
  const isLoading = authLoading || googleLoading || localLoading

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LoginFormData>({
    mode: 'onBlur',
  })

  useEffect(() => {
    if (isAuthenticated) {
      const target = user?.role === 'admin' ? '/admin' : '/dashboard'
      navigate(target)
    }
  }, [isAuthenticated, navigate, user])

  useEffect(() => {
    if (error) {
      toast.error(error)
      dispatch(clearError())
    }
  }, [error, dispatch])

  const onSubmit = async (data: LoginFormData) => {
    const loginType = window.location.pathname.startsWith('/admin') ? 'admin' : 'user'
    setLocalLoading(true)
    try {
      const res = await authService.login({ ...data, loginType })
      if (res.mfaRequired && res.mfa_token) {
        setMfaRequired(true)
        setMfaToken(res.mfa_token)
        toast.success('Two-factor authentication required')
      } else if (res.success && res.data) {
        localStorage.setItem('token', res.data.token)
        localStorage.setItem('authProvider', (res.data.user as any)?.authProvider || 'email')
        dispatch(loginSuccess(res.data.user, res.data.token))
      } else {
        toast.error(res.message || 'Login failed')
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Invalid credentials')
    } finally {
      setLocalLoading(false)
    }
  }

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isBackupMode && !backupCode.trim()) {
      toast.error('Please enter your backup code')
      return
    }
    if (!isBackupMode && !mfaCode.trim()) {
      toast.error('Please enter the 6-digit authentication code')
      return
    }

    setLocalLoading(true)
    try {
      let res: any
      if (isBackupMode) {
        res = await authService.verifyMfaBackup(mfaToken, backupCode)
      } else {
        res = await authService.verifyMfa(mfaToken, mfaCode)
      }

      if (res.success && res.data) {
        localStorage.setItem('token', res.data.token)
        localStorage.setItem('authProvider', (res.data.user as any)?.authProvider || 'email')
        dispatch(loginSuccess(res.data.user, res.data.token))
        toast.success('Access authorized')
      } else {
        toast.error(res.message || 'Verification failed')
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Invalid verification code')
    } finally {
      setLocalLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    try {
      setGoogleLoading(true)
      const { url } = await authService.getGoogleLoginUrl()

      const width = 500
      const height = 600
      const left = window.screen.width / 2 - width / 2
      const top = window.screen.height / 2 - height / 2

      const popup = window.open(
        url,
        'Google Sign In',
        `width=${width},height=${height},left=${left},top=${top}`
      )

      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'GOOGLE_LOGIN_SUCCESS') {
          const { token, user } = event.data
          localStorage.setItem('token', token)
          localStorage.setItem('authProvider', 'google')
          // Dispatch login success to update Redux state
          dispatch(loginSuccess(user, token))
          setGoogleLoading(false)
          window.removeEventListener('message', handleMessage)
        } else if (event.data?.type === 'GOOGLE_LOGIN_ERROR') {
          toast.error(event.data.message || 'Google login failed')
          setGoogleLoading(false)
          window.removeEventListener('message', handleMessage)
        }
      }

      window.addEventListener('message', handleMessage)

      const checkPopup = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(checkPopup)
          window.removeEventListener('message', handleMessage)
          // Only clear loading if no success (which would have navigated/dispatched)
          // Check if authenticated in useEffect or just reset loading
          // If success happened, isAuthenticated will be true soon.
          // If closed without success, we need to reset loading.
          setGoogleLoading((prev) => {
            // This callback runs after closure.
            // If we are authenticated, it doesn't matter (we unmount).
            return false
          })
        }
      }, 500)

    } catch (err) {
      toast.error('Failed to start Google login')
      setGoogleLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-app-bg py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Ambient Glows */}
      <div className="absolute top-[-200px] right-[-100px] w-[600px] h-[600px] bg-primary/8 rounded-full blur-[150px] pointer-events-none"></div>
      <div className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] bg-info/5 rounded-full blur-[150px] pointer-events-none"></div>
      {/* Subtle grid overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px] pointer-events-none"></div>

      <div className="max-w-md w-full space-y-8 z-10">
        <div className="text-center">
          <div className="flex justify-center mb-0">
            <Link to="/" className="flex items-center gap-3 group">
              <img src="/DocSprintLogo.png" alt="DocSprintAI" className="h-16 sm:h-20 w-auto object-contain drop-shadow-[0_0_20px_rgba(255,107,0,0.4)] group-hover:scale-105 transition-transform duration-300" />
            </Link>
          </div>
          <h2 className="text-3xl font-black font-heading text-white mt-[-8px] uppercase tracking-wide">
            {window.location.pathname.startsWith('/admin') ? 'Admin Login' : 'Welcome Back'}
          </h2>
          <p className="mt-2 text-sm text-text-secondary font-medium uppercase tracking-[0.15em] opacity-80">
            {window.location.pathname.startsWith('/admin') ? 'Admin Dashboard Login' : 'Sign in to DocSprintAI to continue'}
          </p>
        </div>

        <Card className="border-white/10 shadow-[0_30px_60px_rgba(0,0,0,0.5)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-center text-sm font-black uppercase tracking-[0.2em] text-white/50">
              {mfaRequired ? (isBackupMode ? 'MFA Backup Verification' : 'Two-Factor Verification') : 'Credential Authentication'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {mfaRequired ? (
              <form onSubmit={handleMfaSubmit} className="space-y-6">
                <p className="text-xs text-text-secondary font-medium leading-relaxed">
                  {isBackupMode
                    ? 'Enter one of your 10-character backup codes. Each code can be used only once.'
                    : 'Enter the 6-digit code generated by your authenticator app to authorize access.'
                  }
                </p>

                {isBackupMode ? (
                  <Input
                    label="Backup Code"
                    type="text"
                    placeholder="e.g. A1B2C3D4E5"
                    value={backupCode}
                    onChange={(e) => setBackupCode(e.target.value)}
                    required
                    maxLength={10}
                    className="text-center font-mono text-lg font-bold tracking-widest uppercase"
                  />
                ) : (
                  <Input
                    label="Authenticator Code"
                    type="text"
                    placeholder="e.g. 123456"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    required
                    maxLength={6}
                    className="text-center font-mono text-xl tracking-[0.3em] font-bold"
                  />
                )}

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => {
                      setMfaRequired(false)
                      setMfaToken('')
                      setMfaCode('')
                      setBackupCode('')
                      setIsBackupMode(false)
                    }}
                    className="text-xs font-black text-text-secondary hover:text-white uppercase tracking-widest transition-colors"
                  >
                    Back to Login
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsBackupMode(!isBackupMode)}
                    className="text-xs font-black text-primary hover:text-primary/80 uppercase tracking-widest transition-colors"
                  >
                    {isBackupMode ? 'Use Authenticator' : 'Use Backup Code'}
                  </button>
                </div>

                <Button type="submit" className="w-full py-4 bg-primary hover:bg-orange-600 shadow-[0_10px_30px_rgba(255,107,0,0.2)]" disabled={isLoading} loading={isLoading}>
                  {isLoading ? 'Verifying...' : 'Authorize Access'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="name@company.com"
                  icon={Mail}
                  error={errors.email?.message}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                />

                <Input
                  label="Security Password"
                  type="password"
                  placeholder="••••••••"
                  icon={Lock}
                  error={errors.password?.message}
                  {...register('password', {
                    required: 'Password is required',
                  })}
                />

                <div className="flex items-center justify-end -mt-4">
                  <Link
                    to="/forgot-password"
                    className="text-xs font-black text-primary hover:text-primary/80 uppercase tracking-widest transition-colors"
                  >
                    Reset Password?
                  </Link>
                </div>

                <Button type="submit" className="w-full py-4 bg-primary hover:bg-orange-600 shadow-[0_10px_30px_rgba(255,107,0,0.2)]" disabled={!isValid || isLoading} loading={isLoading}>
                  {isLoading ? 'Authenticating...' : 'Access Account'}
                </Button>

                <div className="relative mt-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-card text-text-secondary font-medium">Or continue with</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                  className="mt-4 w-full flex items-center justify-center px-4 py-3 border border-white/10 rounded-xl shadow-sm bg-white/5 text-white hover:bg-white/10 hover:border-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/50 ring-offset-app-bg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                  <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  <span className="font-bold text-sm">Sign in with Google</span>
                </button>

                <div className="text-center text-sm space-y-2 pt-4">
                  <div>
                    <span className="text-text-secondary">Don't have an account? </span>
                    <Link to="/signup" className="font-bold text-primary hover:text-primary/80 hover:underline transition-colors">
                      Sign up
                    </Link>
                  </div>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}